import { jsPDF } from "jspdf";
import { LOGO_SAO_JORGE } from "./logoSaoJorge";

// Converte HTML do editor rico em texto simples para o PDF (mantém quebras e itens de lista).
export const htmlToPlain = (html) => {
  if (!html) return "";
  if (!/[<&]/.test(html)) return html;
  return String(html)
    .replace(/<\/(p|div|h[1-6]|tr)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<\/li>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&").replace(/&lt;/gi, "<").replace(/&gt;/gi, ">")
    .replace(/\n{3,}/g, "\n\n").trim();
};

const fmtData = (iso) => {
  if (!iso) {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  const [d] = iso.split("T");
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
};

const azul = [23, 54, 120];
const ouro = [176, 141, 87];
const cinza = [96, 106, 122];
const escuro = [30, 41, 59];

// ---- Folha A4 paisagem (297x210) com DOIS receituários lado a lado ----
const SW = 297, SH = 210;
const COL_L = { x0: 12, w: 128 };
const COL_R = { x0: 157, w: 128 };
const MEIO = 148.5;
const SY = SH - 16;          // linha do rodapé
const Y_MAX = 187;           // limite inferior do conteúdo (acima da assinatura)

// Marca d'água suave: brasão centralizado, bem clarinho, atrás do conteúdo da coluna.
function desenharMarca(doc, empresa, cx0, cw) {
  try {
    const img = empresa?.logo || LOGO_SAO_JORGE;
    const props = doc.getImageProperties(img);
    const ratio = props.width && props.height ? props.width / props.height : 1;
    let w = cw * 0.62, h = w / ratio;
    if (h > 95) { h = 95; w = h * ratio; }
    const x = cx0 + (cw - w) / 2, y = (SH - h) / 2;
    doc.setGState(new doc.GState({ opacity: 0.06 }));
    doc.addImage(img, props.fileType || "PNG", x, y, w, h);
    doc.setGState(new doc.GState({ opacity: 1 }));
  } catch (e) { /* GState/imagem indisponível: ignora marca d'água */ }
}

// Cabeçalho elegante dentro de uma coluna. Retorna Y inicial do conteúdo.
function desenharCabecalho(doc, { empresa, residente, rec, especial }, cx0, cw) {
  const cx1 = cx0 + cw;
  // marca d'água (fundo)
  desenharMarca(doc, empresa, cx0, cw);
  // faixa de destaque
  doc.setFillColor(...azul); doc.rect(cx0, 6, cw, 2.4, "F");
  doc.setFillColor(...ouro); doc.rect(cx0, 8.4, cw, 0.7, "F");

  const yTop = 13;
  let lw = 0, lh = 0;
  try {
    const img = empresa?.logo || LOGO_SAO_JORGE;
    const props = doc.getImageProperties(img);
    const ratio = props.width && props.height ? props.width / props.height : 1;
    lw = 27; lh = lw / ratio;
    if (lh > 23) { lh = 23; lw = lh * ratio; }
    doc.addImage(img, props.fileType || "PNG", cx0, yTop, lw, lh);
  } catch (e) { /* ignora */ }

  const areaX = cx0 + (lw ? lw + 5 : 0);
  const cc = areaX + (cx1 - areaX) / 2;   // centro do bloco de texto do cabeçalho
  // Nome (serifado elegante)
  doc.setFont("helvetica", "bold"); doc.setFontSize(15); doc.setTextColor(...azul);
  doc.text(empresa?.nome || "Residencial São Jorge", cc, yTop + 4, { align: "center" });
  // filete dourado
  doc.setDrawColor(...ouro); doc.setLineWidth(0.7);
  doc.line(cc - 30, yTop + 6.2, cc + 30, yTop + 6.2);
  // dados da empresa (compactos, centralizados)
  doc.setFont("helvetica", "normal"); doc.setFontSize(6); doc.setTextColor(...cinza);
  const infos = [
    `CNPJ: ${empresa?.cnpj || "-"}`,
    empresa?.endereco || "",
    [empresa?.cep ? `CEP: ${empresa.cep}` : "", empresa?.telefone, empresa?.telefone2].filter(Boolean).join("   ·   "),
    [empresa?.site, empresa?.email].filter(Boolean).join("   ·   "),
  ].filter((x) => x && x.trim());
  let hy = yTop + 9.5;
  infos.forEach((l) => { doc.text(l.trim(), cc, hy, { align: "center" }); hy += 3; });

  let y = Math.max(hy, yTop + lh + 1, yTop + 20) + 1;
  // divisória dupla
  doc.setDrawColor(...azul); doc.setLineWidth(0.5); doc.line(cx0, y, cx1, y);
  doc.setDrawColor(...ouro); doc.setLineWidth(0.25); doc.line(cx0, y + 1, cx1, y + 1);
  y += 5.5;

  // Título
  doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor(...escuro);
  doc.text(especial ? "Receituário de Controle Especial" : "Receituário", (cx0 + cx1) / 2, y, { align: "center" });
  y += 5.5;

  // Paciente + CNS
  doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(...escuro);
  doc.text(`Paciente: ${residente?.nome || "-"}`, cx0, y);
  y += 4.4;
  doc.setFont("helvetica", "normal"); doc.setFontSize(8);
  doc.text(`CNS: ${residente?.cns || "-"}`, cx0, y);
  y += 3.8;
  if (rec.usoContinuo) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(...azul);
    doc.text("USO CONTÍNUO", cx0, y + 0.5);
    doc.setTextColor(...escuro); y += 4;
  }
  doc.setDrawColor(225, 228, 235); doc.setLineWidth(0.2); doc.line(cx0, y, cx1, y);
  y += 4.5;
  return y;
}

// Rodapé (Data + assinatura) dentro de uma coluna.
function desenharRodape(doc, { rec }, cx0, cw) {
  const cx1 = cx0 + cw;
  doc.setDrawColor(90, 100, 115); doc.setLineWidth(0.3);
  // Data
  const dX0 = cx0, dX1 = cx0 + 42;
  doc.line(dX0, SY, dX1, SY);
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(...escuro);
  doc.setTextColor(...cinza); doc.text("Data", (dX0 + dX1) / 2, SY + 3.5, { align: "center" });
  // Assinatura
  const pX1 = cx1, pX0 = cx1 - 66, pcx = (pX0 + pX1) / 2;
  doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(160, 168, 180);
  doc.text("Assinatura", pcx, SY - 2, { align: "center" });
  doc.setDrawColor(90, 100, 115); doc.setLineWidth(0.3); doc.setLineDashPattern([1, 1], 0);
  doc.line(pX0, SY, pX1, SY); doc.setLineDashPattern([], 0);
  doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(...escuro);
  doc.text(rec.profissionalNome || "Prescritor", pcx, SY + 3.5, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(...cinza);
  const reg = [rec.conselho || "CRM", rec.registro, rec.registroUf].filter(Boolean).join(" ");
  doc.text(reg || "CRM", pcx, SY + 7, { align: "center" });
}

function divisoriaCentral(doc) {
  doc.setDrawColor(205, 210, 220); doc.setLineWidth(0.3); doc.setLineDashPattern([2, 2], 0);
  doc.line(MEIO, 8, MEIO, SH - 8); doc.setLineDashPattern([], 0);
}

// Constrói os blocos (medicamentos + descritivo + especial) com alturas pré-medidas para a largura cw.
function construirBlocos(doc, ctx, cw, numOffset = 0) {
  const blocos = [];
  (ctx.rec.medicamentos || []).forEach((m, i) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(9.5);
    const nome = [m.nome, m.concentracao].filter(Boolean).join(" ");
    const qtd = m.quantidade ? `  —  ${m.quantidade}` : "";
    const l1 = doc.splitTextToSize(`${i + 1 + numOffset}) ${nome}${qtd}`, cw);
    doc.setFont("helvetica", "normal"); doc.setFontSize(8.5);
    const l2 = m.posologia ? doc.splitTextToSize(m.posologia, cw - 6) : [];
    const h = l1.length * 4.5 + l2.length * 4.1 + 2.6;
    blocos.push({
      h,
      draw: (x, y) => {
        doc.setFont("helvetica", "bold"); doc.setFontSize(9.5); doc.setTextColor(...escuro);
        doc.text(l1, x, y); const yy = y + l1.length * 4.5;
        if (l2.length) {
          doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(70, 80, 95);
          doc.text(l2, x + 6, yy);
        }
      },
    });
  });
  if (ctx.rec.descritivo) {
    doc.setFont("helvetica", "italic"); doc.setFontSize(9);
    const d = doc.splitTextToSize(htmlToPlain(ctx.rec.descritivo), cw);
    blocos.push({
      h: d.length * 4.4 + 3,
      draw: (x, y) => {
        doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(70, 80, 95);
        doc.text(d, x, y + 2); doc.setTextColor(...escuro);
      },
    });
  }
  if (ctx.especial) {
    blocos.push({
      h: 44,
      draw: (x, y, w) => {
        let by = y + 3;
        doc.setDrawColor(220, 224, 232); doc.setLineWidth(0.2); doc.line(x, by, x + w, by); by += 5;
        doc.setFont("helvetica", "bold"); doc.setFontSize(6.5); doc.setTextColor(...cinza);
        doc.text("IDENTIFICAÇÃO DO COMPRADOR", x, by); by += 5;
        doc.setFont("helvetica", "normal"); doc.setFontSize(6);
        ["Nome:", "RG / Órgão emissor:", "Endereço:", "Cidade/UF - Telefone:"].forEach((l) => {
          doc.setTextColor(...cinza); doc.text(l, x, by);
          doc.setDrawColor(205, 210, 220); doc.line(x + 30, by, x + w, by); by += 5;
        });
        by += 1;
        doc.setFont("helvetica", "bold"); doc.setFontSize(6.5); doc.setTextColor(...cinza);
        doc.text("IDENTIFICAÇÃO DO FORNECEDOR", x, by); by += 4.5;
        doc.setFont("helvetica", "normal"); doc.setFontSize(6);
        ["Farmácia / Data:", "Assinatura do farmacêutico:"].forEach((l) => {
          doc.setTextColor(...cinza); doc.text(l, x, by);
          doc.setDrawColor(205, 210, 220); doc.line(x + 40, by, x + w, by); by += 5;
        });
      },
    });
  }
  return blocos;
}

const novoDoc = () => new jsPDF({ orientation: "landscape", unit: "mm", format: [SW, SH] });
const salvar = (doc, residente, sufixo) =>
  doc.save(`receituario-${sufixo}-${(residente?.nome || "paciente").replace(/\s+/g, "_")}.pdf`);

// 2 vias / especial: as duas colunas SEMPRE idênticas, uma ao lado da outra.
function fluir2Colunas(doc, ctx) {
  const blocos = construirBlocos(doc, ctx, COL_L.w);
  let i = 0;
  while (true) {
    const yL = desenharCabecalho(doc, ctx, COL_L.x0, COL_L.w);
    const yR = desenharCabecalho(doc, ctx, COL_R.x0, COL_R.w);
    let y = yL;
    for (; i < blocos.length; i++) {
      if (y + blocos[i].h > Y_MAX) break;
      blocos[i].draw(COL_L.x0, y, COL_L.w);
      blocos[i].draw(COL_R.x0, yR + (y - yL), COL_R.w);
      y += blocos[i].h;
    }
    desenharRodape(doc, ctx, COL_L.x0, COL_L.w);
    desenharRodape(doc, ctx, COL_R.x0, COL_R.w);
    if (i >= blocos.length) break;
    doc.addPage([SW, SH], "landscape");
  }
}

// Simples: 1 via na coluna esquerda; ao exceder, segue para a coluna do lado; depois nova folha.
function fluirSimples(doc, ctx) {
  const blocos = construirBlocos(doc, ctx, COL_L.w);
  const cols = [COL_L, COL_R];
  let ci = 0, i = 0;
  const abrir = () => {
    return desenharCabecalho(doc, ctx, cols[ci].x0, cols[ci].w);
  };
  let y = abrir();
  for (; i < blocos.length; i++) {
    if (y + blocos[i].h > Y_MAX) {
      desenharRodape(doc, ctx, cols[ci].x0, cols[ci].w);
      ci++;
      if (ci >= cols.length) { doc.addPage([SW, SH], "landscape"); ci = 0; }
      y = abrir();
    }
    blocos[i].draw(cols[ci].x0, y, cols[ci].w);
    y += blocos[i].h;
  }
  desenharRodape(doc, ctx, cols[ci].x0, cols[ci].w);
}

export function gerarReceitaSimples({ empresa, residente, rec }) {
  const doc = novoDoc();
  fluirSimples(doc, { empresa, residente, rec, especial: false });
  salvar(doc, residente, "simples");
}

export function gerarReceita2Vias({ empresa, residente, rec }) {
  const doc = novoDoc();
  fluir2Colunas(doc, { empresa, residente, rec, especial: false });
  salvar(doc, residente, "2vias");
}

// ==================== RECEITUÁRIO DE CONTROLE ESPECIAL ====================
// Reproduz fielmente o modelo: título "RECEITUÁRIO CONTROLE ESPECIAL",
// bloco IDENTIFICAÇÃO DO EMITENTE (logo + dados) à esquerda, nota das vias +
// box "Carimbo e Ass. do Profissional" no canto superior direito, e no rodapé
// dois boxes lado a lado (COMPRADOR à esquerda, FORNECEDOR à direita).
const PRETO = [20, 20, 20];
const BORDA = [110, 110, 110];
const BARRA = [224, 224, 224];

// Rodapé: dois boxes (Comprador | Fornecedor) dentro de uma via.
function desenharBoxesEspecial(doc, x0, w) {
  const x1 = x0 + w;
  const top = 150, bottom = SH - 4, h = bottom - top; // 150..206
  const compW = w * 0.6;
  const compX0 = x0, compX1 = x0 + compW;
  const fornX0 = compX1, fornX1 = x1, fornW = fornX1 - fornX0;
  const pad = 3;

  // Molduras
  doc.setDrawColor(...BORDA); doc.setLineWidth(0.3);
  doc.rect(compX0, top, compW, h);
  doc.rect(fornX0, top, fornW, h);
  // Barras de título
  doc.setFillColor(...BARRA);
  doc.rect(compX0, top, compW, 6, "F");
  doc.rect(fornX0, top, fornW, 6, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(7); doc.setTextColor(...PRETO);
  doc.text("IDENTIFICAÇÃO DO COMPRADOR", compX0 + compW / 2, top + 4.1, { align: "center" });
  doc.text("IDENTIFICAÇÃO DO FORNECEDOR", fornX0 + fornW / 2, top + 4.1, { align: "center" });

  // --- Campos do COMPRADOR ---
  doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(...PRETO);
  doc.setLineWidth(0.2);
  const rotulo = (txt, lx, ly, endX) => {
    doc.text(txt, lx, ly);
    const lw = txt ? doc.getTextWidth(txt) + 1 : 0;
    doc.setDrawColor(...PRETO);
    doc.line(lx + lw, ly, endX, ly);
  };
  let cy = top + 13;
  rotulo("Nome:", compX0 + pad, cy, compX1 - pad); cy += 6.5;
  doc.setDrawColor(...PRETO); doc.line(compX0 + pad, cy, compX1 - pad, cy); cy += 6.5; // continuação nome
  // Ident. + Órg. Emissor (mesma linha)
  const meio = compX0 + compW * 0.58;
  rotulo("Ident.:", compX0 + pad, cy, meio - 2);
  rotulo("Órg. Emissor:", meio, cy, compX1 - pad); cy += 6.5;
  rotulo("Endereço:", compX0 + pad, cy, compX1 - pad); cy += 6.5;
  doc.setDrawColor(...PRETO); doc.line(compX0 + pad, cy, compX1 - pad, cy); cy += 6.5; // continuação endereço
  // Cidade + UF
  const meio2 = compX0 + compW * 0.68;
  rotulo("Cidade:", compX0 + pad, cy, meio2 - 2);
  rotulo("UF:", meio2, cy, compX1 - pad); cy += 6.5;
  rotulo("Tel:", compX0 + pad, cy, compX1 - pad);

  // --- FORNECEDOR (assinatura do farmacêutico + data) ---
  const sigY = bottom - 13;
  doc.setDrawColor(...PRETO); doc.setLineWidth(0.3);
  doc.line(fornX0 + 4, sigY, fornX1 - 4, sigY);
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(...PRETO);
  doc.text("Assinatura do Farmacêutico", fornX0 + fornW / 2, sigY + 4, { align: "center" });
  doc.text("Data  ____/____/________", fornX0 + fornW / 2, sigY + 9, { align: "center" });
}

// Desenha UMA via completa do receituário especial.
function drawViaEspecial(doc, ctx, med, numero, x0, w) {
  const { empresa, residente } = ctx;
  const x1 = x0 + w;

  // Marca d'água suave do brasão
  desenharMarca(doc, empresa, x0, w);

  // Título
  doc.setFont("helvetica", "bold"); doc.setFontSize(12.5); doc.setTextColor(...PRETO);
  doc.text("RECEITUÁRIO CONTROLE ESPECIAL", (x0 + x1) / 2, 11, { align: "center" });

  // ---- IDENTIFICAÇÃO DO EMITENTE ----
  const leftW = w * 0.58;
  const eY = 15;
  doc.setFillColor(...BARRA); doc.rect(x0, eY, leftW, 6.2, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(...PRETO);
  doc.text("IDENTIFICAÇÃO DO EMITENTE", x0 + leftW / 2, eY + 4.2, { align: "center" });

  // Nota das vias (lado direito, topo)
  const rX = x0 + leftW + 3;
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.8); doc.setTextColor(...PRETO);
  doc.text("1ª via Retenção na Farmácia ou Drogaria", rX, eY + 2.5);
  doc.text("2ª via Orientação ao Paciente", rX, eY + 5.7);

  // Box do emitente (logo + dados)
  const boxTop = eY + 6.2, boxH = 34;
  doc.setDrawColor(...BORDA); doc.setLineWidth(0.3);
  doc.rect(x0, boxTop, leftW, boxH);
  let ly = boxTop + 2.5;
  try {
    const img = empresa?.logo || LOGO_SAO_JORGE;
    const props = doc.getImageProperties(img);
    const ratio = props.width && props.height ? props.width / props.height : 1;
    let lw = leftW * 0.66, lh = lw / ratio;
    if (lh > 17) { lh = 17; lw = lh * ratio; }
    doc.addImage(img, props.fileType || "PNG", x0 + (leftW - lw) / 2, ly, lw, lh);
    ly += lh + 2;
  } catch (e) { ly += 4; }
  doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(...PRETO);
  doc.text(`CNPJ: ${empresa?.cnpj || "-"}`, x0 + leftW / 2, ly, { align: "center" }); ly += 3.4;
  doc.setFont("helvetica", "normal"); doc.setFontSize(6.5); doc.setTextColor(70, 70, 70);
  if (empresa?.endereco) { doc.text(empresa.endereco, x0 + leftW / 2, ly, { align: "center" }); ly += 3; }
  const l3 = [empresa?.cep ? `CEP: ${empresa.cep}` : "", empresa?.telefone].filter(Boolean).join(" - ");
  if (l3) { doc.text(l3, x0 + leftW / 2, ly, { align: "center" }); }

  // Carimbo e assinatura do profissional (canto superior direito)
  const cyLine = boxTop + boxH - 6;
  doc.setDrawColor(...PRETO); doc.setLineWidth(0.3);
  doc.line(rX, cyLine, x1 - 1, cyLine);
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(...PRETO);
  doc.text("Carimbo e Ass. do Profissional", (rX + x1 - 1) / 2, cyLine + 4, { align: "center" });

  // ---- Paciente ----
  let y = boxTop + boxH + 6.5;
  doc.setFont("helvetica", "bold"); doc.setFontSize(10.5); doc.setTextColor(...PRETO);
  doc.text(`Paciente: ${residente?.nome || "-"}`, x0, y);
  y += 6.5;
  // Endereço (por padrão o endereço da empresa)
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.text("Endereço", x0, y);
  const enderecoPaciente = "Rua São Miguel Arcanjo, 148 - Qta da Paineira - São Paulo - CEP: 03152-050";
  doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(50, 50, 50);
  const endLines = doc.splitTextToSize(enderecoPaciente, w - 22);
  doc.text(endLines, x0 + 18, y);
  y += Math.max(5.5, endLines.length * 3.4 + 2.5);
  // CNS (linha própria, abaixo do endereço)
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...PRETO);
  doc.text(`CNS: ${residente?.cns || "-"}`, x0, y);
  y += 6.5;

  // ---- Prescrição ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...PRETO);
  doc.text("Prescrição:", x0, y);
  y += 6.5;
  if (med?.nome) {
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...PRETO);
    const nome = [med?.nome, med?.concentracao].filter(Boolean).join(" ");
    const l1 = doc.splitTextToSize(`${numero}.  ${nome} ${".".repeat(48)}`, w);
    doc.text(l1, x0, y); y += l1.length * 4.4 + 1;
    if (med?.posologia) {
      doc.setFontSize(8.5); doc.setTextColor(55, 55, 55);
      const l2 = doc.splitTextToSize(med.posologia, w - 10);
      doc.text(l2, x0 + 6, y); y += l2.length * 4;
    }
    // Preenche o espaço livre entre a medicação e os boxes com "X" em negrito,
    // centralizado, para impedir a inclusão manual de outras medicações.
    const boxesTop = 150;
    doc.setFont("helvetica", "bold"); doc.setFontSize(14); doc.setTextColor(...PRETO);
    let xy = y + 8;
    while (xy < boxesTop - 5) {
      doc.text("X", x0 + w / 2, xy, { align: "center" });
      xy += 7.5;
    }
  }
  // Sem medicação: deixa a área em branco para preenchimento manual à caneta.

  // ---- Rodapé: boxes Comprador / Fornecedor ----
  desenharBoxesEspecial(doc, x0, w);
}

// Especial: 1 medicação por folha, em 2 vias idênticas lado a lado (numeração = posição original).
function fluirEspecial(doc, ctx) {
  const all = ctx.rec.medicamentos || [];
  const itens = all.map((m, i) => ({ m, n: i + 1 })).filter((x) => (x.m.nome || "").trim());
  const lista = itens.length ? itens : [{ m: { nome: "", posologia: "" }, n: 1 }];
  lista.forEach((it, idx) => {
    if (idx > 0) doc.addPage([SW, SH], "landscape");
    drawViaEspecial(doc, ctx, it.m, it.n, COL_L.x0, COL_L.w);
    drawViaEspecial(doc, ctx, it.m, it.n, COL_R.x0, COL_R.w);
  });
}

export function gerarReceitaEspecial({ empresa, residente, rec }) {
  const doc = novoDoc();
  fluirEspecial(doc, { empresa, residente, rec, especial: true });
  salvar(doc, residente, "especial");
}
